/**
 * Mbp module api — wires the passive GPT + Prebid + Voltax video observers.
 * There is nothing for the host to call; the module works entirely on its own.
 * (Time-on-site is kernel-owned — `core/analytics/timeOnSite.ts` — and runs on
 * every activity.)
 */

import { AwaitFor, awaitFor } from '../../core/events/awaiter';
import { armKzqEmergencyFlush, closeKzqStub, flushKzqAdBuffer } from '../../core/kzq';
import type { SdkContext } from '../types';
import {
  observeGpt,
  observePrebid,
  replayKzqAdEvent,
  _resetMbpObserversForTests,
} from './observers';
import { observeVoltaxPlayers, _resetVideoObserversForTests } from './videoObservers';

export interface MbpModuleApi {
  /** True once the observers are attached. */
  readonly active: boolean;
}

export function createMbpModuleApi(_ctx: SdkContext): MbpModuleApi {
  observeGpt();
  observePrebid();
  observeVoltaxPlayers();
  closeKzqStub();
  // State exists from here, so a pagehide flush can go through the real
  // handlers — replacing init's minimal ad_imp-only sender.
  armKzqEmergencyFlush((events) => {
    for (const entry of events) {
      try {
        replayKzqAdEvent(entry);
      } catch {
        /* ignore */
      }
    }
  });
  // Modules install before init resolves identity, so the buffer waits for READY
  // — that is the earliest point an ad event can carry a real session.
  awaitFor(AwaitFor.READY, () => flushKzqAdBuffer(replayKzqAdEvent));
  return { active: true };
}

/** Test-only */
export function _resetMbpForTests(): void {
  _resetMbpObserversForTests();
  _resetVideoObserversForTests();
}
